import { useState } from 'react';
import { Badge, Button, Checkbox, ControlRow, FormField, Input, Modal, ModalBody, ModalFooter, ModalHeader, Select, useToast } from '@vd/ui';
import { ALL_MCP_SCOPES, DEFAULT_MCP_SCOPES, MCP_SCOPES, MCP_SCOPE_LABELS, isReadOnlyScopeSet, type McpScope } from '@vd/types';
import { Check, Copy, KeyRound, AlertTriangle } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCurrentCompany } from '../auth/AuthProvider';
import { api } from '../../lib/api';
import { copyToClipboard } from '../../lib/clipboard';
import { ProjectMark } from '../projects/ProjectMark';

export interface CreateMcpTokenModalProps {
  open: boolean;
  onClose: () => void;
}

type Stage = 'form' | 'reveal';

export function CreateMcpTokenModal({ open, onClose }: CreateMcpTokenModalProps) {
  const [stage, setStage] = useState<Stage>('form');
  const [label, setLabel] = useState('');
  // What an MCP client holding the token may do (TOADO-470). Read is always
  // granted: a token that cannot read can do nothing else either.
  const [mcpScopes, setMcpScopes] = useState<Set<McpScope>>(() => new Set(DEFAULT_MCP_SCOPES));
  const readOnly = isReadOnlyScopeSet([...mcpScopes]);
  const [expiresInDays, setExpiresInDays] = useState<'never' | '30' | '90' | '365'>('90');
  const [selectedProjects, setSelectedProjects] = useState<Set<string>>(new Set());
  const [companyWide, setCompanyWide] = useState(true);
  const [secret, setSecret] = useState('');
  const [copied, setCopied] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();
  const qc = useQueryClient();
  const currentCompany = useCurrentCompany();
  const companyId = currentCompany?.id;

  const projectsQ = useQuery({
    queryKey: ['projects', companyId],
    queryFn: () => companyId ? api.listProjects(companyId) : Promise.resolve([]),
    enabled: open && !!companyId,
  });

  const reset = () => {
    setStage('form');
    setLabel('');
    setMcpScopes(new Set(DEFAULT_MCP_SCOPES));
    setExpiresInDays('90');
    setSelectedProjects(new Set());
    setCompanyWide(true);
    setSecret('');
    setCopied(false);
  };

  const close = () => {
    onClose();
    // Delay to let the close animation finish before state wipe.
    setTimeout(reset, 250);
  };

  const create = async () => {
    if (!label.trim() || submitting) return;
    if (companyWide && !companyId) {
      setError('No current company. Sign in first.');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const result = await api.createToken({
        label: label.trim(),
        scope: {
          companies: companyWide && companyId ? [companyId] : undefined,
          projects: companyWide ? undefined : [...selectedProjects],
          readOnly,
          mcp: ALL_MCP_SCOPES.filter((scope) => mcpScopes.has(scope)),
        },
        expiresInDays: expiresInDays === 'never' ? null : Number(expiresInDays),
      });
      setSecret(result.secret);
      setStage('reveal');
      await qc.invalidateQueries({ queryKey: ['tokens'] });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  const onCopy = async () => {
    await copyToClipboard(secret);
    setCopied(true);
    setTimeout(() => setCopied(false), 1400);
  };

  return (
    <Modal open={open} onClose={stage === 'reveal' ? () => {} : close} width={480} dismissible={stage !== 'reveal'}>
      <ModalHeader>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <KeyRound size={16} />
          {stage === 'form' ? 'Create MCP token' : 'Copy your token'}
        </span>
      </ModalHeader>

      {stage === 'form' ? (
        <>
          <ModalBody>
            <FormField label="Label" help="A human-readable name so you can identify this token later.">
              <Input autoFocus value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Jason's laptop · Claude Code" />
            </FormField>

            <div style={{ height: 14 }} />

            <FormField label="Scope">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <ControlRow>
                  <Checkbox
                    checked={companyWide}
                    onChange={(e) => setCompanyWide(e.target.checked)}
                  />
                  <span>Grant access to <strong>all projects</strong> in this company</span>
                </ControlRow>
                {!companyWide && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, paddingLeft: 24 }}>
                    {projectsQ.data?.map((p) => (
                      <ControlRow key={p.id}>
                        <Checkbox
                          checked={selectedProjects.has(p.id)}
                          onChange={(e) => {
                            setSelectedProjects((s) => {
                              const next = new Set(s);
                              if (e.target.checked) next.add(p.id);
                              else next.delete(p.id);
                              return next;
                            });
                          }}
                        />
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          <ProjectMark project={p} size={14} />
                          {p.name}
                        </span>
                      </ControlRow>
                    ))}
                  </div>
                )}
              </div>
            </FormField>

            <div style={{ height: 14 }} />

            <FormField label="Permissions" help="What an MCP client using this token may do.">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {ALL_MCP_SCOPES.map((scope) => (
                  <ControlRow key={scope}>
                    <Checkbox
                      checked={mcpScopes.has(scope)}
                      disabled={scope === MCP_SCOPES.READ}
                      onChange={(e) => {
                        setMcpScopes((current) => {
                          const next = new Set(current);
                          if (e.target.checked) next.add(scope);
                          else next.delete(scope);
                          return next;
                        });
                      }}
                    />
                    <span>
                      {MCP_SCOPE_LABELS[scope].title}
                      <span style={{ display: 'block', color: 'var(--text-tertiary)', fontSize: 12 }}>
                        {MCP_SCOPE_LABELS[scope].desc}
                      </span>
                    </span>
                  </ControlRow>
                ))}
              </div>
            </FormField>

            <div style={{ height: 14 }} />

            <FormField label="Expires">
              <Select value={expiresInDays} onChange={(e) => setExpiresInDays(e.target.value as typeof expiresInDays)}>
                <option value="never">Never</option>
                <option value="30">In 30 days</option>
                <option value="90">In 90 days</option>
                <option value="365">In 1 year</option>
              </Select>
            </FormField>
            {error && <div style={{ color: 'var(--danger)', fontSize: 12, marginTop: 8 }}>{error}</div>}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={close}>Cancel</Button>
            <Button
              variant="primary"
              disabled={!label.trim() || (!companyWide && selectedProjects.size === 0) || submitting}
              loading={submitting}
              onClick={create}
            >
              Create token
            </Button>
          </ModalFooter>
        </>
      ) : (
        <>
          <ModalBody>
            <div style={{
              padding: 12,
              background: 'var(--warning-bg)', color: 'var(--warning)',
              border: '1px solid var(--warning-border)', borderRadius: 8,
              display: 'flex', gap: 10, alignItems: 'flex-start',
              fontSize: 13,
              marginBottom: 16,
            }}>
              <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 2 }} />
              <div>
                <strong style={{ display: 'block' }}>Copy this token now.</strong>
                <span style={{ color: 'var(--text-secondary)' }}>
                  We’ll never show it again. If you lose it, you’ll need to create a new one.
                </span>
              </div>
            </div>

            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 6, color: 'var(--text-secondary)' }}>Label</div>
              <div style={{ fontSize: 14 }}>{label}</div>
            </div>

            <div>
              <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 6, color: 'var(--text-secondary)' }}>Token secret</div>
              <div style={{
                display: 'flex', gap: 6,
                fontFamily: 'JetBrains Mono, monospace',
                fontSize: 12,
                padding: '10px 12px',
                background: 'var(--bg-muted)',
                border: '1px solid var(--border)',
                borderRadius: 6,
                alignItems: 'center',
              }}>
                <code style={{ flex: 1, wordBreak: 'break-all' }}>{secret}</code>
                <Button
                  variant="secondary"
                  size="sm"
                  leftIcon={copied ? <Check size={12} /> : <Copy size={12} />}
                  onClick={onCopy}
                >
                  {copied ? 'Copied' : 'Copy'}
                </Button>
              </div>
            </div>

            <div style={{ marginTop: 14, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <Badge variant="outline">{companyWide ? 'All projects' : `${selectedProjects.size} projects`}</Badge>
              <Badge variant={readOnly ? 'outline' : 'solid'}>{readOnly ? 'Read only' : 'Read / write'}</Badge>
              <Badge variant="outline">{expiresInDays === 'never' ? 'No expiry' : `Expires in ${expiresInDays}d`}</Badge>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="primary"
              onClick={() => {
                toast.push({ variant: 'success', title: 'Token created', description: label });
                close();
              }}
            >
              I’ve copied the token
            </Button>
          </ModalFooter>
        </>
      )}
    </Modal>
  );
}
